import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Box, Typography, Grid } from '@material-ui/core';
import LoadingSpinner from 'components/LoadingSpinner';
import { Label, Message } from 'components/shared';
import { slugify } from 'utils/string-helpers';
import PerformanceCell from '../../PerformanceWidget/components/PerformanceCell';

const useStyles = makeStyles(theme => {
  const { palette } = theme;
  return {
    root: {
      backgroundColor: palette.grey.light,
      border: `1px solid ${palette.grey.medium}`,
      padding: '8px',
      marginTop: '8px'
    },
    header: {
      color: palette.grey.dark,
      paddingBottom: '6px',
      fontSize: '1.3rem',
      borderBottom: `3px solid ${palette.accent.main}`,
      textTransform: 'uppercase'
    },
    body: {
      display: 'grid',
      gridTemplateColumns: '1fr 1.5fr',
      columnGap: '20px',
      lineHeight: '2em',
      paddingTop: '6px'
    },
    compactBody: {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding
    },
    label: {
      color: palette.grey.dark
    },
    compactLabel: {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding
    },
    value: {
      color: palette.primary.main
    },
    compactValue: {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding
    },
    managerPerformance: {
      fontWeight: 'bold',
      marginTop: '5px',
      paddingBottom: '5px',
      textAlign: 'center'
    }
  };
});

const HoldingDetailsTable = ({ widget, state, setTableExportData, forwardRef }) => {
  const classes = useStyles();
  const { data: response, isLoading, isError, error } = state;
  const { overlay } = widget;
  const { widgetTitle } = overlay;

  useEffect(() => {
    if (response) {
      setTableExportData({
        title: widgetTitle && slugify(widgetTitle),
        columns: [],
        rows: response.data
      });
    }
  }, [response, setTableExportData, widgetTitle]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  const renderValue = item => {
    const { value } = item;
    const spaceSeparator = /[\r\n]+/;

    return value.split(spaceSeparator).map(rowContent => (
      <Typography key={rowContent} className={classes.compactValue} size="small">
        {rowContent}
      </Typography>
    ));
  };

  const performanceData = () => {
    return (
      <>
        <Typography className={classes.managerPerformance}>Manager Performance</Typography>
        <Grid container direction="row-reverse" alignItems="center" justify="center">
          {response &&
            response.holdingManagerPerformanceData.length > 0 &&
            response.holdingManagerPerformanceData.map(value => (
              <Grid item xs={4} key={value.text} style={{ padding: '4px' }}>
                <PerformanceCell {...value} />
              </Grid>
            ))}
        </Grid>
      </>
    );
  };
  const renderData = () => {
    return response.data.map(item => (
      <Grid
        key={item.label}
        container
        direction="row"
        justify="center"
        alignItems="flex-start"
        spacing={0}
      >
        <Grid item xs={5}>
          <Typography className={classes.compactLabel}>{item.label}</Typography>
        </Grid>
        <Grid item xs={7}>
          {renderValue(item)}
        </Grid>
      </Grid>
    ));
  };

  return (
    <Box ref={forwardRef} className={classes.root}>
      {renderData()}
      {performanceData()}
    </Box>
  );
};

HoldingDetailsTable.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string
        })
      )
    })
  }),
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectType: PropTypes.string,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

HoldingDetailsTable.defaultProps = {
  state: {},
  setTableExportData: null,
  forwardRef: null
};

export default HoldingDetailsTable;
